import {
  DatePickerField,
  FormProvider,
  ReadOnlyField,
  SelectField,
  SimpleGrid,
  Stack,
  useForm,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { CROP_PLEDGE_USER_ROLES } from '@bcba/types';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { CommonUpdateCropPledgeCreditorInformationDto } from '../../../../services/crop-pledge/dto/update';

import { formatDate } from '@bcba/common-utils';
import { CropPledgeSectionActions } from '../../shared/CropPledgeSectionActions';
import { useCropPledgeCreditorContext } from './context/CropPledgeCreditorContext';

const resolver = classValidatorResolver(
  CommonUpdateCropPledgeCreditorInformationDto,
);

export const PledgeCreditorPFForm = () => {
  const {
    selectedCreditorData,
    nationalitiesOptions,
    maritalStatusOptions,
    setCreditorInfo,
    isPendingSetCreditorInfo,
    approveSection,
    isPendingSectionApproval,
    role,
    creditorSectionStatus,
    isFormReadOnly,
  } = useCropPledgeCreditorContext();

  const {
    fiscalId,
    representantFirstName,
    representantLastName,
    representantNationality,
    representantBirthDate,
    representantMaritalStatus,
    representantIdentityDocumentType,
    representantIdentityDocumentId,
    streetName,
    streetNumber,
    province,
    locality,
    postalCode,
    email,
    phoneNumber,
  } = selectedCreditorData ?? {};

  const { t } = useTranslation();

  const formMethods = useForm<CommonUpdateCropPledgeCreditorInformationDto>({
    resolver,
    defaultValues: {
      isPFCompanyType: true,
      representantNationality: representantNationality || undefined,
      representantBirthDate: representantBirthDate || undefined,
      representantMaritalStatus: representantMaritalStatus || undefined,
    },
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const onApprove = (data: CommonUpdateCropPledgeCreditorInformationDto) => {
    if (role === CROP_PLEDGE_USER_ROLES.CREDITOR) {
      approveSection(data);
    }
  };

  return (
    <Stack mt={6} gap={6}>
      <SimpleGrid columns={2} spacing={6}>
        <ReadOnlyField label={t('common.CUIT/CUIL')} value={fiscalId ?? ''} />
        <ReadOnlyField
          label={t('common.name')}
          value={representantFirstName || ''}
        />
        <ReadOnlyField
          label={t('common.lastName')}
          value={representantLastName || ''}
        />
        <ReadOnlyField
          label={t('common.documentType')}
          value={representantIdentityDocumentType || ''}
        />
        <ReadOnlyField
          label={t('common.document')}
          value={representantIdentityDocumentId || ''}
        />
        {!isFormReadOnly ? (
          <FormProvider {...formMethods}>
            <SelectField
              options={nationalitiesOptions}
              label={t('common.nationality.label')}
              placeholder={t('common.nationality.placeholder')}
              name="representantNationality"
              rhfOptions={{ required: true }}
              errorMessageFormatter={t}
            />
            <DatePickerField
              name="representantBirthDate"
              label={t('common.birthDate.label')}
              placeholder={t('common.birthDate.placeholder')}
              rhfOptions={{ required: true }}
              errorMessageFormatter={t}
            />
            <SelectField
              options={maritalStatusOptions}
              label={t('common.maritalStatus.label')}
              placeholder={t('common.maritalStatus.placeholder')}
              name="representantMaritalStatus"
              rhfOptions={{ required: true }}
              errorMessageFormatter={t}
            />
          </FormProvider>
        ) : (
          <>
            <ReadOnlyField
              label={t('common.nationality.label')}
              value={
                representantNationality
                  ? t(`nationality.${representantNationality}`)
                  : t('common.toBeCompletedByCreditor')
              }
            />
            <ReadOnlyField
              label={t('common.birthDate.label')}
              value={
                representantBirthDate
                  ? formatDate(representantBirthDate)
                  : t('common.toBeCompletedByCreditor')
              }
            />
            <ReadOnlyField
              label={t('common.maritalStatus.label')}
              value={
                representantMaritalStatus
                  ? t(`common.maritalStatus.${representantMaritalStatus}`)
                  : t('common.toBeCompletedByCreditor')
              }
            />
          </>
        )}
        <ReadOnlyField
          label={t('businessSignUp.form.addressStreet.label')}
          value={streetName || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.addressNumber.label')}
          value={streetNumber || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.state.label')}
          value={province || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.locality.label')}
          value={locality || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.zipCode.label')}
          value={postalCode || ''}
        />
        <ReadOnlyField label={t('common.email')} value={email || ''} />
        <ReadOnlyField label={t('common.phone')} value={phoneNumber || ''} />
      </SimpleGrid>
      <CropPledgeSectionActions
        isFormDirty={isDirty}
        sectionStatus={creditorSectionStatus!.status}
        onRequestApproval={() => setCreditorInfo({ fiscalId: fiscalId ?? '' })}
        isPendingRequestApproval={isPendingSetCreditorInfo}
        onApprove={handleSubmit(onApprove)}
        isPendingApprove={isPendingSectionApproval}
      />
    </Stack>
  );
};
